/**
 * Approval gate for Arabic copy that carries regulatory or consent weight.
 *
 * Most of the Arabic bundle comes straight from the nsave Arabic Localization
 * Pack and ships as-is. Three areas do not: they were authored or adapted here
 * because the pack supplies no approved wording, and publishing them would put
 * statements in front of users that neither legal nor compliance has signed off.
 *
 * Rather than hold the whole `/ar` rollout, each of those areas is listed here
 * and serves the **approved English** copy until its flag flips. Arabic visitors
 * see an English cookie banner, FAQ and footer disclosure — visibly unfinished,
 * but never an unapproved regulatory claim. The drafts stay in `ar/ui.ts` and
 * `ar/shared.ts` so reviewers have something concrete to sign off on.
 *
 * Flipping a flag to `true` is the sign-off. Do it only with the approval in
 * hand, and only for the area that was approved — `localized.spec.ts` asserts
 * that anything still `false` is being served in English.
 */
export const AR_COPY_APPROVED = {
  /**
   * Footer regulatory disclosure. Pack section 27 calls its own Arabic "a
   * linguistic draft only" and requires replacement, verbatim, with text nsave's
   * legal department and the relevant regulators approve.
   */
  legal: false,
  /**
   * Cookie-consent banner. The pack flags the banner as needing an Arabic
   * re-check (checklist item 10) but supplies no copy, so the Arabic here is
   * authored in-house and has had no consent-language review.
   */
  cookies: false,
  /**
   * Homepage / business FAQ answers. The pack supplies the seven questions
   * (section 9) but no answers; the answers were adapted from wording the pack
   * approves elsewhere, including the segregated-funds sentence in section 8.
   * Those are regulatory claims and need product-compliance review.
   */
  faq: false,
} as const satisfies Record<string, boolean>;
